import { AREA_SCAN_DEFAULTS } from "../schemas/modules.js";
/**
 * The checks a technician can run inside a visit.
 *
 * Coarse on purpose. Working at heights is one thing to a technician and five
 * recorded facts underneath, so it is one module whose `metadata` carries the
 * five - not five modules the picker makes somebody reassemble.
 *
 * One `assessments` row per module per visit, and several rows for one module is
 * normal: PRD §6 step 5 repeats the cycle every time the ladder moves.
 *
 * `ASSESSMENT_MODULE_SEED` is what `npm run bootstrap` writes into
 * `public.assessment_modules`; clients read the table. The **codes** stay here
 * because both clients branch on them - `area_scan` opens a camera, `heights`
 * opens the inclinometer - so a code is a contract rather than a label.
 */

export const ASSESSMENT_MODULES = ["area_scan", "heights", "ppe", "utility"] as const;

export type AssessmentModuleCode = (typeof ASSESSMENT_MODULES)[number];

/** One entry in a module's checklist. Carries its own label so neither client translates. */
export interface ModuleChecklistItem {
  code: string;
  label: string;
}

export interface AssessmentModuleSeed {
  code: AssessmentModuleCode;
  label: string;
  blurb: string;
  /**
   * Records a prompt rather than a detection. Nothing about an advisory module
   * may read as a measurement - PRD §10 makes that a contract term.
   */
  advisory: boolean;
  /**
   * The module's own checklist, written to `assessment_modules.config`.
   *
   * This is the column that exists so a checklist is **data** rather than a
   * constant only the console can read. Both clients render straight from it.
   */
  config: Record<string, unknown>;
}

export const ASSESSMENT_MODULE_SEED: readonly AssessmentModuleSeed[] = [
  {
    code: "area_scan",
    label: "Area scan",
    blurb: "Sweep the work area for 20 to 30 seconds",
    advisory: false,
    config: { ...AREA_SCAN_DEFAULTS },
  },
  {
    code: "heights",
    label: "Working at heights",
    blurb: "Ladder angle, footing, tie-off and condition",
    advisory: false,
    config: {
      target_angle_deg: 75.5,
      tolerance_deg: 2,
      /**
       * How far either side of the target the phone will take the reading on
       * its own, once it has been left still for five seconds - 70.5° to 80.5°.
       *
       * **Deliberately wider than `tolerance_deg`.** A ladder three degrees out
       * is still a ladder somebody has set and the record has to be able to say
       * so; what this band excludes is a phone at rest against something that
       * is not a ladder at all. Outside it the technician records the reading
       * with a tap instead, and nothing on the record distinguishes the two -
       * the fact being stored is the angle, not how it was kept.
       *
       * Console-side this is unused: it governs capture, and the console never
       * captures.
       */
      auto_capture_band_deg: 5,
      /**
       * Everything here except the `attested` codes below goes to the detector
       * over the photographs of the set-up, exactly as PPE's `items` do. The
       * angle above is the one fact on this module that does not: it is a
       * reading, and readings are not inferred.
       *
       * The attested one sorts last so the screen can ask for it after the
       * detector has answered everything it can.
       */
      checks: [
        { code: "base_level", label: "Feet level on the ground" },
        { code: "tie_off", label: "Tied off at the top" },
        { code: "condition", label: "Rails and rungs undamaged" },
        { code: "extension", label: "Three feet above the landing" },
        { code: "ground_firm", label: "Ground is firm enough" },
      ],
      /**
       * Nothing optical measures soil bearing capacity and the design partner
       * has already conceded the point, so this one can only ever be a human
       * tick. It must never be pre-filled or inferred - the analyze endpoint
       * takes this list out of what it asks the detector about, and the phone
       * renders it as a plain switch after the detector's rows.
       */
      attested: ["ground_firm"],
    },
  },
  {
    code: "ppe",
    label: "PPE check",
    blurb: "Front camera, before the climb",
    advisory: false,
    config: {
      items: [
        { code: "hard_hat", label: "Hard hat" },
        { code: "safety_glasses", label: "Safety glasses" },
        { code: "gloves", label: "Gloves" },
        { code: "hi_vis", label: "Hi-vis" },
        { code: "boots", label: "Boots" },
        { code: "harness", label: "Harness" },
        { code: "lanyard", label: "Lanyard" },
      ],
    },
  },
  {
    code: "utility",
    label: "Utility awareness",
    blurb: "Visible outlets, panels and fixtures near the drill point",
    advisory: true,
    /**
     * Deliberately empty. The one-tap chips on the phone's capture screen
     * (`suggestions`) are the customer's list, set on the console's Modules
     * page, and there is no sensible stand-in: until an administrator adds
     * some, `moduleConfigured` refuses the module rather than asking a
     * question nobody chose.
     */
    config: {},
  },
] as const;

export function isAssessmentModuleCode(value: unknown): value is AssessmentModuleCode {
  return typeof value === "string" && (ASSESSMENT_MODULES as readonly string[]).includes(value);
}

/**
 * How a module is drawn and run. Client-side, keyed off `code`.
 *
 * None of this is a column, and the icon is why the rule is easy to see: the web
 * needs a Lucide name and the phone a Material one, so a single `icon` column
 * could never have held both. `capture` is the same shape of thing - it names
 * which screen the client opens, which is a client's own business.
 *
 * `order` is the order the flow runs them in, and the order the picker lists
 * them. The phone mirrors this by hand when the capture flow lands.
 */
export const MODULE_PRESENTATION: Record<
  AssessmentModuleCode,
  {
    /** Lucide name on the web. The phone's Material equivalent is its own map. */
    icon: string;
    capture: "video" | "sensor" | "selfie" | "form";
    order: number;
    defaultSelected: boolean;
    /** Budget in seconds. The selected set has to sum under NFR_TARGETS.assessmentSeconds. */
    targetSeconds: number;
  }
> = {
  area_scan: {
    icon: "scan-search",
    capture: "video",
    order: 10,
    defaultSelected: true,
    targetSeconds: 45,
  },
  heights: {
    icon: "triangle-right",
    capture: "sensor",
    order: 20,
    defaultSelected: true,
    // Two steps now - the reading, then the photographs the set-up checks are
    // read from - so this is no longer thirty seconds. The default set sums to
    // 100s against a 90s target; whichever way that is resolved (a shorter
    // sweep, or a longer budget) is a product call, not this file's.
    targetSeconds: 40,
  },
  ppe: { icon: "hard-hat", capture: "selfie", order: 30, defaultSelected: true, targetSeconds: 15 },
  utility: { icon: "zap", capture: "form", order: 40, defaultSelected: false, targetSeconds: 20 },
};

/** A module added after this shipped sorts last rather than first. */
export function moduleOrder(code: string): number {
  return isAssessmentModuleCode(code) ? MODULE_PRESENTATION[code].order : 999;
}
